import styled from "styled-components";
import {useState} from "react";
import {useAppStore} from "../../../StateManager";
import {featureTitles} from "../../../data/state/AppStateConstants";
import {XyiconFeature, Permission} from "../../../generated/api/base";
import {ButtonV5} from "../button/ButtonV5";
import {colorPalette} from "../styles/colorPalette";
import type {View} from "../../../data/models/View";
import {StringUtils} from "../../../utils/data/string/StringUtils";
import {baseDistance, FLEXCENTER, fontWeight} from "../styles/styles";
import type {WorkspaceViewType} from "../navigation/views/WorkspaceViewCommon";
import {CreateViewPopup} from "../abstract/view/create/CreateViewPopup";
import type {Report} from "../../../data/models/Report";
import {ReportPanelV5} from "../modules/report/create/ReportPanelV5";

interface IEmptyListViewProps {
	readonly onAddClick?: () => void;
	readonly feature?: XyiconFeature;
	readonly text?: React.ReactNode;
	readonly isDetailsContent?: boolean;
	readonly runReport?: (report: Report, justUpdate?: boolean) => void;
}

export const EmptyListViewV5 = (props: IEmptyListViewProps) => {
	const appState = useAppStore((state) => state.appState);
	const {feature, onAddClick, text, isDetailsContent} = props;
	const moduleName = featureTitles[feature] || "";
	const views: View[] = (appState.actions.getViews(feature) ?? []).toSorted((a: View, b: View) => StringUtils.sortIgnoreCase(a.name, b.name));
	const hasPermission = appState.actions.hasAnyTypeTheGivenPermissionInModule(feature, Permission.Update);
	const [createViewPopupType, setCreateViewPopupType] = useState<WorkspaceViewType | null>(null);
	const [newReport, setNewReport] = useState<Report>(null);

	const toggleCreateViewPopup = () => {
		setCreateViewPopupType((prevState) => (prevState ? null : "My Views"));
	};

	return (
		<>
			<EmptyListViewStyled className="EmptyListView">
				<div className="content">
					{([XyiconFeature.Portfolio, XyiconFeature.XyiconCatalog].includes(feature) ||
						(feature === XyiconFeature.Space && !isDetailsContent && !onAddClick)) &&
					views.length === 0 ? (
						<p className="description">{text || `No views to display..`}</p>
					) : feature === XyiconFeature.View ? (
						<>
							<div className="description">
								<div className="title">No Active View</div>
								<div className="message">Select a view from the navigation menu or create a new one.</div>
							</div>
							<ButtonV5
								label="Create"
								className="createView"
								onClick={toggleCreateViewPopup}
							/>
						</>
					) : (
						<>
							<div className="description">
								{feature === XyiconFeature.Space ? <p>No {moduleName ? `${moduleName}s` : ""}</p> : ""}
								{text || `There are no ${feature === XyiconFeature.Space ? "" : moduleName || ""} items to list.`}
								{onAddClick && <span>Start by adding your first.</span>}
							</div>
							{onAddClick && (
								<ButtonV5
									label="Create"
									disabled={!hasPermission}
									className={`${moduleName}`}
									onClick={onAddClick}
								/>
							)}
						</>
					)}
				</div>
			</EmptyListViewStyled>
			{createViewPopupType && (
				<CreateViewPopup
					onClose={toggleCreateViewPopup}
					type={createViewPopupType}
					viewFolderIdToSaveNewViewInto={""}
					onCreateReportView={(report) => {
						setNewReport(report);
					}}
				/>
			)}
			{newReport && (
				<ReportPanelV5
					isReportView={true}
					report={newReport}
					onRunReport={props.runReport}
					onClose={(saveChanges: boolean, report: Report) => {
						setNewReport(null);
						if (!saveChanges) {
							report?.cancelEditing();
						}
					}}
				/>
			)}
		</>
	);
};

const EmptyListViewStyled = styled.div`
	${FLEXCENTER};
	flex: 1;
	width: 304px;
	padding: ${baseDistance.md};
	margin: auto;

	.content {
		width: 100%;

		div.description {
			text-align: center;
			line-height: 24px;

			.title {
				font-size: 24px;
				font-weight: ${fontWeight.bold};
				margin-bottom: 24px;
			}

			.message {
				color: ${colorPalette.gray.c700Dark};
				margin-bottom: 24px;
			}

			p {
				font-weight: ${fontWeight.bold};
				margin-bottom: ${baseDistance.md};
			}

			span {
				${FLEXCENTER};
				width: 100%;
			}
		}

		.Space {
			background-color: ${colorPalette.primary.c500Primary};
			color: ${colorPalette.white};
		}

		.Button {
			background-color: ${colorPalette.primary.c500Primary};
			color: ${colorPalette.white};
			margin-top: ${baseDistance.md};
			height: 40px;

			&.disabled {
				color: ${colorPalette.gray.c200Light};
				border-color: ${colorPalette.gray.c200Light};
			}

			svg {
				color: ${colorPalette.primary.c500Primary};
			}
		}
	}
`;
